import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useMarketplaceBase } from '@/contexts/MarketplaceBaseContext';
import { ShoppingCart, ReceiptText, Store } from 'lucide-react';

/** Marketplace toolbar (browse / orders / cart) shared by every surface. */
export function MarketplaceShell({ subtitle, children }: { subtitle?: string; children: ReactNode }) {
  const base = useMarketplaceBase();
  const { count } = useCart();
  const { pathname } = useLocation();
  const isActive = (p: string) => (p === base ? pathname === base : pathname.startsWith(p));

  const tab = (to: string, label: string, Icon: typeof Store) => (
    <Link
      to={to}
      className={
        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ' +
        (isActive(to) ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50')
      }
    >
      <Icon className="h-4 w-4" /> {label}
    </Link>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketplace</h1>
          <p className="text-sm text-gray-500">{subtitle ?? 'Products & services from companies across every industry.'}</p>
        </div>
        <nav className="flex items-center gap-1">
          {tab(base, 'Browse', Store)}
          {tab(`${base}/orders`, 'Orders', ReceiptText)}
          <Link to={`${base}/cart`} className="relative inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700">
            <ShoppingCart className="h-4 w-4" /> Cart
            {count > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">{count}</span>}
          </Link>
        </nav>
      </div>
      {children}
    </div>
  );
}
